import { apiClient, type Paginated } from '@/core/api';
import type {
  AuthUser,
  GoogleLoginInput,
  LoginInput,
  LoginResponse,
  MfaSetupResponse,
  MfaStatus,
  OrganizationDto,
} from './types';

export async function login(input: LoginInput): Promise<LoginResponse> {
  const { data } = await apiClient.post<LoginResponse>('/auth/login/', input);
  return data;
}

/** Wave 9 — Google SSO: exchange a Google access_token/code for our JWT pair. */
export async function loginGoogle(input: GoogleLoginInput): Promise<LoginResponse> {
  const { data } = await apiClient.post<LoginResponse>('/auth/google/', input);
  return data;
}

// ---- MFA (TOTP) — used by SecurityPage ---------------------------------------

export async function getMfaStatus(): Promise<MfaStatus> {
  const { data } = await apiClient.get<MfaStatus>('/auth/mfa/status/');
  return data;
}

export async function startMfaSetup(): Promise<MfaSetupResponse> {
  const { data } = await apiClient.post<MfaSetupResponse>('/auth/mfa/setup/');
  return data;
}

export async function confirmMfa(code: string): Promise<MfaStatus> {
  const { data } = await apiClient.post<MfaStatus>('/auth/mfa/confirm/', { code });
  return data;
}

export async function disableMfa(code: string): Promise<MfaStatus> {
  const { data } = await apiClient.post<MfaStatus>('/auth/mfa/disable/', { code });
  return data;
}

export async function logout(): Promise<void> {
  // Best-effort server logout; the client clears tokens regardless.
  try {
    await apiClient.post('/auth/logout/');
  } catch {
    /* ignore — local sign-out still proceeds */
  }
}

export async function getCurrentUser(): Promise<AuthUser> {
  const { data } = await apiClient.get<AuthUser>('/users/current-user/');
  return data;
}

// ---- Password reset / set (invite "set password on first login") ------------

/** Request a reset/set-password email (also used by "Forgot password?"). */
export async function requestPasswordReset(email: string): Promise<void> {
  await apiClient.post('/auth/password/reset/', { email });
}

export interface PasswordResetConfirmInput {
  uid: string;
  token: string;
  new_password1: string;
  new_password2: string;
}

/** Set the password from an emailed invite/reset link (uid + token). */
export async function confirmPasswordReset(input: PasswordResetConfirmInput): Promise<void> {
  await apiClient.post('/auth/password/reset/confirm/', input);
}

export interface PasswordChangeInput {
  old_password: string;
  new_password1: string;
  new_password2: string;
}

/** Change the signed-in user's password (dj-rest-auth PasswordChangeView). */
export async function changePassword(input: PasswordChangeInput): Promise<void> {
  await apiClient.post('/auth/password/change/', input);
}

export async function getMyOrganizations(): Promise<OrganizationDto[]> {
  const { data } = await apiClient.get<Paginated<OrganizationDto> | OrganizationDto[]>(
    '/organizations/',
  );
  return Array.isArray(data) ? data : data.results;
}

/** Extract the access/refresh pair regardless of dj-rest-auth key naming. */
export function tokensFrom(res: LoginResponse): { access: string; refresh: string } {
  return {
    access: res.access ?? res.access_token ?? '',
    refresh: res.refresh ?? res.refresh_token ?? '',
  };
}
